import { ImageField } from "./image-field";

/** The shapes a before/after image is actually cut to: the wide comparison frame, and a square card. */
const PROJECT_PREVIEWS = [
  { label: "Before / after", aspect: 553 / 317 },
  { label: "Card", aspect: 1 },
];
import { CharacterCount } from "./character-count";
import { ColourField } from "./colour-field";
import { RoomTypeSelect } from "./room-type-select";

/**
 * A before/after project: the pair of images, the thumbnail, and what the member wants to say
 * about it.
 *
 * BOTH IMAGES ARE REQUIRED and the server refuses a pair missing one, keyed to beforeImage or
 * afterImage so each message lands on the field that is actually empty. The form does not
 * pre-empt that check — it is the same rule, and one copy of it is enough.
 *
 * The room type IS here, as a proper labelled field using the site's own Select — the same
 * RoomTypeSelect the CREATE PROJECT modal uses, where the design has the member choose it first.
 * It stays here too so the room can be changed later. The meta row still SHOWS the chosen room,
 * because the design puts it there; it just does not pretend to be the input.
 *
 * Field order follows the design: about, then the before/after pair, then the thumbnail, then the
 * colours used.
 */
export function ProjectFields({ form, update, errors, settings, copy }) {
  return (
    <>
      <div className="member-profile-editor__field">
        <span className="member-profile-editor__field-label" id="project-room-type-label">
          {copy.roomTypeLabel}
        </span>

        <RoomTypeSelect
          roomTypes={settings.roomTypes}
          value={form.roomTypeKey}
          onChange={(value) => update({ roomTypeKey: value })}
          placeholder={copy.roomTypePlaceholder}
          labelledBy="project-room-type-label"
          testId="project-room-type"
        />

        {errors.roomType ? (
          <p className="member-profile-editor__error" role="alert" data-testid="error-room-type">
            {errors.roomType}
          </p>
        ) : null}
      </div>

      <div className="member-profile-editor__field">
        <label className="member-profile-editor__field-label" htmlFor="project-description">
          {copy.projectAboutLabel}
        </label>

        <p className="member-profile-editor__hint">{copy.projectAboutHint}</p>

        <textarea
          id="project-description"
          className="member-profile-editor__textarea"
          data-testid="project-description"
          rows={6}
          value={form.description}
          onChange={(event) => update({ description: event.target.value })}
        />

        <CharacterCount
          value={form.description}
          max={settings.descriptionMaxLength}
          testId="project-description-counter"
        />

        {errors.description ? (
          <p className="member-profile-editor__error" role="alert" data-testid="error-description">
            {errors.description}
          </p>
        ) : null}
      </div>

      {/* One heading over the pair, with "Before" and "After" as the tiles' own small labels —
          which is how the design draws it, and which is also true: they are one decision. */}
      <div className="member-profile-editor__group">
        <p className="member-profile-editor__field-label">{copy.projectImagesLabel}</p>
        <p className="member-profile-editor__hint">{copy.projectImagesHint}</p>

        <div className="member-profile-editor__pair">
          <ImageField
            label={copy.beforeLabel}
            images={form.beforeImage}
            onChange={(beforeImage) => update({ beforeImage })}
            maxFileSizeBytes={settings.imageMaxFileSizeBytes}
            testId="project-before-image"
            error={errors.beforeImage}
            copy={copy}
            source="project"
            previews={PROJECT_PREVIEWS}
          />

          <ImageField
            label={copy.afterLabel}
            images={form.afterImage}
            onChange={(afterImage) => update({ afterImage })}
            maxFileSizeBytes={settings.imageMaxFileSizeBytes}
            testId="project-after-image"
            error={errors.afterImage}
            copy={copy}
            source="project"
            previews={PROJECT_PREVIEWS}
          />
        </div>
      </div>

      <ImageField
        label={copy.projectThumbnailLabel}
        hint={copy.projectThumbnailHint}
        images={form.thumbnail}
        onChange={(thumbnail) => update({ thumbnail })}
        maxFileSizeBytes={settings.imageMaxFileSizeBytes}
        testId="project-thumbnail"
        error={errors.thumbnail}
        copy={copy}
        source="project"
      />

      {/* The caption is not drawn on the edit frame, but it IS rendered publicly — it is the
          subtitle under the project title on the public profile. That was briefly untrue: this
          comment used to point at the intro panel of the five-panel project carousel, and when
          PublicProjects replaced that deck with the tabs-and-toggle layout the caption lost its
          only rendering site while this input went on collecting it. Dropping the input would
          leave a field that shows on the profile and can never be filled in. */}
      <div className="member-profile-editor__field">
        <label className="member-profile-editor__field-label" htmlFor="project-caption">
          {copy.projectCaptionLabel}
        </label>

        <input
          id="project-caption"
          type="text"
          className="member-profile-editor__input"
          data-testid="project-caption"
          value={form.caption}
          onChange={(event) => update({ caption: event.target.value })}
        />

        <CharacterCount
          value={form.caption}
          max={settings.captionMaxLength}
          testId="project-caption-counter"
        />

        {errors.caption ? (
          <p className="member-profile-editor__error" role="alert" data-testid="error-caption">
            {errors.caption}
          </p>
        ) : null}
      </div>

      {/* The section heading and helper are the shell's, not the field's — ColourField renders the
          two entry pills and the chosen grid, and nothing else. */}
      <div className="member-profile-editor__group">
        <p className="member-profile-editor__field-label">{copy.coloursUsedLabel}</p>
        <p className="member-profile-editor__hint">{copy.coloursUsedHint}</p>

        <ColourField
          colours={form.colours}
          onChange={(colours) => update({ colours })}
          error={errors.coloursUsed}
          settings={settings}
          copy={copy}
        />
      </div>
    </>
  );
}
